import DuoFunnelLayout from '@/components/v2/DuoFunnelLayout'
import DuoMascot from '@/components/v2/DuoMascot'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'
import type { StepScreenProps } from './types'

const DEFAULT_DURATION_MS = 3000

/**
 * The plan-building loader: a filling bar that auto-advances, timed like the
 * shared `ProgressPage` (the bar reaches 100% a second before the submit). The
 * hidden field keeps that page's tracked name/value shape too.
 */
export default function LoaderStep({
  bodyKey,
  durationMs = DEFAULT_DURATION_MS,
  interpolation,
  progressLabelKey,
  step,
  titleKey,
  totalSteps,
}: StepScreenProps & {
  bodyKey?: string
  durationMs?: number
  progressLabelKey?: string
  titleKey: string
}) {
  const [progress, setProgress] = useState(0)

  const formRef = useRef<HTMLFormElement>(null)
  const submit = useSubmit()
  const { t } = useTranslation()

  useEffect(() => {
    const interval = setInterval(
      () => setProgress((previous) => Math.min(previous + 1, 100)),
      (durationMs - 1000) / 100,
    )

    const timeout = setTimeout(() => {
      if (formRef.current) submit(formRef.current)
    }, durationMs)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [durationMs, submit])

  return (
    <DuoFunnelLayout step={step} totalSteps={totalSteps}>
      <div className="flex grow flex-col justify-center gap-8">
        <DuoMascot className="mx-auto h-24 w-24" />

        <div className="space-y-2 text-center">
          <h1 className="text-duo-ink text-2xl font-extrabold text-balance">
            {t(titleKey, interpolation)}
          </h1>

          {bodyKey && (
            <p className="text-duo-muted text-[15px] leading-relaxed">
              {t(bodyKey, interpolation)}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <div className="bg-duo-line h-4 overflow-hidden rounded-full">
            <div
              className="bg-duo-primary h-full rounded-full transition-[width] duration-200 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center justify-between">
            {progressLabelKey && (
              <p className="text-duo-muted text-sm font-bold">
                {t(progressLabelKey, interpolation)}
              </p>
            )}

            <p className="text-duo-primary ml-auto text-sm font-extrabold">
              {progress}%
            </p>
          </div>
        </div>
      </div>

      <Form method="post" ref={formRef}>
        <input
          name={t(titleKey, { lng: 'en', ...interpolation })}
          type="hidden"
          value={t('shared.continue', { lng: 'en' })}
        />
      </Form>
    </DuoFunnelLayout>
  )
}
